<template>
  <div
    class="location-selector"
    :class="{ 'is-focus': isFocus, 'is-show': !showlocation }"
  >
    <y-text
      v-if="showLabel"
      class="title"
      font="var(--y-regular-font)"
      color="#949494"
      size="14px"
    >
      {{ t("shared.hotel.search.location") }}
    </y-text>
    <el-autocomplete
      :disabled="locationDisabled"
      v-model="locationRef"
      :fetch-suggestions="querySearch"
      clearable
      hide-loading
      class="location-input"
      valueKey="showLabel"
      :placeholder="t('shared.hotel.search.locationPlaceholder')"
      @select="handleSelect"
      @blur="handleBlur"
      @focus="handleFocus"
      ref="autocompleteRef"
    >
      <template #prefix>
        <font-icon class="search-icon" icon="icon-Location" />
      </template>
      <template #default="{ item }">
        <div class="item flex">
          <y-icon :name="iconMaps[item.locationType]" />
          <div class="item-label ml-2">
            {{ item.showLabel }}
            <div
              class="hotel-name-en"
              v-if="item.showHotelEnName && item.nameEn"
            >
              {{ item.nameEn }}
            </div>
          </div>
        </div>
      </template>
    </el-autocomplete>
  </div>
</template>

<script setup>
import common from "@charmdeer/common";
import _ from "lodash";
import { computed, onActivated, onMounted, ref } from "vue";
import { nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import { searchUniversalViaPost } from "@/api/biz/common";
import { getRestCity, getRestTpHotel } from "@/api/rest/common";
import { useHotelStoreHook } from "@/distributions/consumer/y/store/hotel";
import { useUserStoreHook } from "@/store/modules/user";
import { queryToParams } from "@/utils/tool";
const { t } = useI18n();
const router = useRouter();
const userStore = useUserStoreHook();
const YHotelStore = useHotelStoreHook();

const _queryString = ref("");

defineProps({
  showLabel: {
    type: Boolean,
    default: true
  },
  modelValue: {
    type: String,
    default: ""
  },
  locationDisabled: {
    type: Boolean,
    default: false
  },
  showlocation: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(["update:modelValue", "change"]);

const locationRef = ref(
  _.get(YHotelStore, "hotelSearchModel.location._id", null)
);

const isFocus = ref(false);
const autocompleteRef = ref(null);

const emitBySelect = ref(false);

const showHotelEnName = computed(() => {
  return userStore.language !== "en";
});

const iconMaps = {
  googleMapPolitical: "icon-location",
  city: "icon-location",
  moduleTpHotel: "icon-hotel",
  search: "icon-search"
};

const querySearch = async (queryString, cb) => {
  if (_queryString.value === queryString) {
    cb([]);
    return;
  }
  emitBySelect.value = false;
  _queryString.value = queryString;
  const result = [
    {
      locationType: "search",
      showLabel: queryString,
      _id: queryString
    }
  ];
  if (queryString && queryString !== "null") {
    const res = await searchUniversalViaPost(queryString);
    const { city, moduleTpHotel, googleMapPolitical } = res;
    city.forEach(item => {
      item.locationType = "city";
      item.showLabel = common.city.toString.name(item, userStore.language);
      item.nameEn = common.city.toString.country(item, userStore.language);
      item.showHotelEnName = true;
    });
    const googleMapPoliticalResult = [];
    if (googleMapPolitical?.length) {
      googleMapPolitical.forEach(item => {
        const hasCity = city.find(
          cityItem => cityItem.showLabel === item.description
        );
        if (!hasCity) {
          item.locationType = "googleMapPolitical";
          item.showLabel = item.description;
          item._id = item.place_id;
          googleMapPoliticalResult.push(item);
        }
      });
    }
    moduleTpHotel.forEach(item => {
      item.locationType = "moduleTpHotel";
      item.showLabel = item.name;
      item.showHotelEnName = showHotelEnName.value;
    });
    result.push(...city, ...googleMapPoliticalResult, ...moduleTpHotel);
  } else {
    cb([]);
    emit("change");
    return;
  }
  cb(result);
};

const handleSelect = item => {
  emitBySelect.value = true;
  emit("update:modelValue", item._id);
  emit("change", item);
};

const handleBlur = async () => {
  isFocus.value = false;
  if (emitBySelect.value) {
    return;
  }
  if (locationRef.value) {
    const item = {
      locationType: "search",
      showLabel: locationRef.value,
      _id: locationRef.value
    };
    handleSelect(item);
  } else {
    emit("update:modelValue", null);
    emit("change", null);
  }
};

const locationValueMaps = {
  city: {
    locationValue: () => {
      return common.city.toString.name(
        YHotelStore.hotelSearchModel.location,
        userStore.language
      );
    },
    getDefaultValue: async _id => {
      const _city = await getRestCity(_id);
      return {
        ..._city,
        locationType: "city",
        showLabel: common.city.toString.name(_city, userStore.language)
      };
    }
  },
  moduleTpHotel: {
    locationValue: () => {
      return common.moduleTpHotel.toString.name(
        YHotelStore.hotelSearchModel.location,
        userStore.language
      );
    },
    getDefaultValue: async _id => {
      const _hotel = await getRestTpHotel(_id);
      return {
        ..._hotel,
        locationType: "moduleTpHotel",
        showLabel: common.moduleTpHotel.toString.name(
          _hotel,
          userStore.language
        )
      };
    }
  },
  googleMapPolitical: {
    locationValue: () => {
      return YHotelStore.hotelSearchModel.location.showLabel;
    },
    getDefaultValue: async (_id, showLabel) => {
      return {
        locationType: "googleMapPolitical",
        place_id: _id,
        description: showLabel,
        _id,
        showLabel
      };
    }
  },
  search: {
    locationValue: () => {
      return YHotelStore.hotelSearchModel.location.showLabel;
    },
    getDefaultValue: async (_id, showLabel) => {
      return {
        locationType: "search",
        description: showLabel,
        _id,
        showLabel
      };
    }
  }
};

const init = async () => {
  if (locationRef.value) {
    if (YHotelStore.hotelSearchModel.location) {
      const { locationType } = YHotelStore.hotelSearchModel.location;
      const _locationValue = locationValueMaps[locationType]?.locationValue?.();
      if (_locationValue) {
        locationRef.value = _locationValue;
      }
    }
  } else {
    const _model = queryToParams() || {};
    const { locationType, _id, showLabel } = _model.location;
    const result = await locationValueMaps[locationType]?.getDefaultValue?.(
      _id,
      showLabel
    );
    if (result && result._id) {
      locationRef.value = result.showLabel;
      handleSelect(result);
    }
  }
};

const handleFocus = event => {
  isFocus.value = true;
  nextTick(() => {
    const input = event.target;
    input.setSelectionRange(input.value.length, input.value.length);
  });
};

onMounted(() => {
  init();
});
onActivated(() => {
  const _pageForward = router.options.history.state.forward;
  if (!_pageForward) {
    init();
  }
});
</script>

<style lang="scss" scoped>
.location-selector {
  --input-direction: rtl;
  width: 188px;

  .search-icon {
    font-size: 18px;
    color: #000000;
  }

  .title {
    margin-bottom: 6px;
    line-height: 1;
    display: block;
  }
  :deep(.el-input.is-disabled .el-input__wrapper) {
    box-shadow: none;
  }
  :deep(.el-input__wrapper) {
    box-shadow: none;
    padding-left: 0;
    padding-right: 0;

    .el-input__inner {
      color: var(--y-text-color);
      font-size: 18px;
      line-height: 20px;
      font-family: var(--y-medium-font);
      direction: var(--input-direction);
      text-align: left;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &.is-focus {
    --input-direction: ltr;
  }
}
.is-show {
  width: 0;
  height: 0;
  opacity: 0;
}
.item {
  line-height: 1;
  padding: 16px 0;
  .y-icon {
    margin-top: 1px;
  }
  .item-label {
    line-height: 16px;
  }
  .hotel-name-en {
    font-weight: 400;
    font-size: 14px;
    color: #949494;
    margin-top: 8px;
    line-height: 16px;
  }
}
</style>
